import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface GoogleBadgeConfig {
  merchantId?: string;
  isEnabled?: boolean;
}

export class GoogleBadgeProvider implements Provider {
  type = ProviderType.GOOGLE_BADGE;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: GoogleBadgeConfig;

  constructor(config: GoogleBadgeConfig = {}) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    // 默认在本地环境启用日志
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;
      if (!this.isEnabled) return false;

      // 先初始化renderBadge函数（脚本加载完成后会自动调用）
      this.setupRenderBadge();

      // 加载Google Platform SDK，这种没有track的函数，不需要等待
      this.loadGooglePlatformScript();

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Google Badge:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    // Google Badge主要用于显示评分徽章，不需要track特定事件
    if (this.loggingEnabled) {
      logger.log(
        `Google Badge: Track method called - Google Badge displays rating badge automatically, ${JSON.stringify(
          event,
        )}`,
      );
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  enableLog(): void {
    this.loggingEnabled = true;
  }

  disableLog(): void {
    this.loggingEnabled = false;
  }

  /**
   * 加载Google Platform SDK脚本
   */
  private async loadGooglePlatformScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const scriptUrl = 'https://apis.google.com/js/platform.js?onload=renderBadge';

        // 检查脚本是否已存在
        const existingScript = document.querySelector(
          `script[src="${scriptUrl}"]`,
        ) as HTMLScriptElement;
        if (existingScript) {
          if (this.loggingEnabled) {
            logger.log('Google Badge: Script already exists');
          }
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = scriptUrl;

        script.onload = () => {
          if (this.loggingEnabled) {
            logger.log('Google Badge: Platform script loaded successfully');
          }
          resolve();
        };

        script.onerror = () => {
          const error = 'Failed to load Google Platform script';
          logger.error('Google Badge:', error);
          reject(new Error(error));
        };

        document.body.appendChild(script);

        if (this.loggingEnabled) {
          logger.log('Google Badge: Script element added to body');
        }
      } catch (error) {
        logger.error('Google Badge: Error creating script element:', error);
        reject(error);
      }
    });
  }

  /**
   * 设置renderBadge函数
   */
  private setupRenderBadge(): void {
    try {
      const merchantId = this.config.merchantId || env.NEXT_PUBLIC_GOOGLE_BADGE_ID;
      const { loggingEnabled } = this;

      if (!merchantId) {
        logger.warn('Google Badge: Merchant ID is not configured');
        return;
      }

      // 定义renderBadge函数
      (window as any).renderBadge = function () {
        try {
          const ratingBadgeContainer = document.createElement('div');
          document.body.appendChild(ratingBadgeContainer);

          if ((window as any).gapi && (window as any).gapi.load) {
            (window as any).gapi.load('ratingbadge', function () {
              if (
                (window as any).gapi &&
                (window as any).gapi.ratingbadge &&
                (window as any).gapi.ratingbadge.render
              ) {
                (window as any).gapi.ratingbadge.render(ratingBadgeContainer, {
                  merchant_id: merchantId,
                  position: 'BOTTOM_LEFT',
                });

                if (loggingEnabled) {
                  logger.log('Google Badge: Rating badge rendered successfully');
                }
              }
            });
          }
        } catch (error) {
          logger.error('Google Badge: Error rendering badge:', error);
        }
      };

      if (this.loggingEnabled) {
        logger.log('Google Badge: renderBadge function set up');
      }
    } catch (error) {
      logger.error('Google Badge: Error setting up renderBadge:', error);
    }
  }
}
